import type {
  Lead,
  LeadsRating,
} from "@/nango/types/apps.autoleads/autoleads/AutoLeads"

// Display names of the review sites the ratings plugin reads. Keys are the
// lowercased source the backend stores; anything else is capitalized as is.
export const RATING_SOURCE_LABELS: Record<string, string> = {
  google: "Google",
  tripadvisor: "Tripadvisor",
  pagesjaunes: "PagesJaunes",
}

export function ratingSourceLabel(source: string): string {
  const key = source.trim().toLowerCase()
  return RATING_SOURCE_LABELS[key] ?? key.charAt(0).toUpperCase() + key.slice(1)
}

/** A rated source as the lead card shows it. */
export type RatingRow = {
  // Empty for the legacy single rating, which carries no source.
  source: string
  rating: number
  reviewsCount: number | null
  sourceUrl: string
}

export type RatingSummary = {
  rows: RatingRow[]
  // Review-count-weighted mean, or the plain mean when no site gave a count.
  average: number
  // Sum of the review counts; null when no site gave one.
  reviewsCount: number | null
}

function reviewsOf(row: { reviews_count: number | null }): number {
  return row.reviews_count ?? 0
}

/**
 * One entry per site: the same site can come back under two spellings
 * ("Google", "google"), so they are merged case-insensitively and the entry
 * with the most reviews wins.
 */
export function dedupeRatings(ratings: readonly LeadsRating[]): LeadsRating[] {
  const bySource = new Map<string, LeadsRating>()
  for (const entry of ratings) {
    const key = entry.source.trim().toLowerCase()
    const kept = bySource.get(key)
    if (!kept || reviewsOf(entry) > reviewsOf(kept)) bySource.set(key, entry)
  }
  return [...bySource.values()]
}

function toRows(lead: Pick<Lead, "rating" | "reviews_count" | "ratings">) {
  const rated = dedupeRatings(lead.ratings ?? []).flatMap((entry) =>
    entry.rating === null
      ? []
      : [
          {
            source: entry.source,
            rating: entry.rating,
            reviewsCount: entry.reviews_count,
            sourceUrl: entry.source_url,
          },
        ],
  )
  if (rated.length > 0) return rated
  // No ratings plugin result: the rating read at discovery stands alone.
  return lead.rating === null
    ? []
    : [
        {
          source: "",
          rating: lead.rating,
          reviewsCount: lead.reviews_count,
          sourceUrl: "",
        },
      ]
}

function weightedAverage(rows: readonly RatingRow[]): number {
  const weight = rows.reduce((sum, row) => sum + (row.reviewsCount ?? 0), 0)
  if (weight === 0) {
    return rows.reduce((sum, row) => sum + row.rating, 0) / rows.length
  }
  return (
    rows.reduce((sum, row) => sum + row.rating * (row.reviewsCount ?? 0), 0) /
    weight
  )
}

/** Every rating of a lead folded into one figure, sites by review count. */
export function summarizeRatings(
  lead: Pick<Lead, "rating" | "reviews_count" | "ratings">,
): RatingSummary | null {
  const rows = toRows(lead).sort(
    (a, b) => (b.reviewsCount ?? 0) - (a.reviewsCount ?? 0),
  )
  if (rows.length === 0) return null
  const counted = rows.filter((row) => row.reviewsCount !== null)
  return {
    rows,
    average: weightedAverage(rows),
    reviewsCount:
      counted.length > 0
        ? counted.reduce((sum, row) => sum + (row.reviewsCount ?? 0), 0)
        : null,
  }
}
